/* eslint-disable @typescript-eslint/indent */
import { Button, Checkbox, FormControl, FormErrorMessage, FormLabel, HStack, Input, InputGroup, Select, Text, Textarea, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { CreateUserMutationVariables, EmailInput, PhoneInput, useCreateUserMutation, useUserTypesQuery } from "../graphql"

type CreateUserFormValues = Omit<CreateUserMutationVariables["input"], "email" | "phone"> & { email?: EmailInput; phone?: PhoneInput }

const validationSchema = yup.object<Record<keyof CreateUserFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	name: yup.string().required().label("Name"),
	email: yup
		.object({
			address: yup.string().nullable().label("Address"),
		})
		.nullable()
		.label("Email"),
	phone: yup
		.object({
			number: yup.string().nullable().label("Number"),
			countryCode: yup.string().nullable().label("Country Code"),
		})
		.nullable()
		.label("Phone"),
	address: yup.string().nullable().label("Address"),
	referenceNo: yup.string().required().label("Reference No"),
	typeId: yup.string().required().label("Type"),
	shouldMarkActiveOnPunchIn: yup.boolean().nullable().label("Mark Active on Punch In"),
	shouldMarkInactiveOnPunchOut: yup.boolean().nullable().label("Mark Inactive on Punch Out"),
})

const initialValues: CreateUserFormValues = {
	name: "",
	typeId: "",
	email: undefined,
	phone: {
		countryCode: "91",
		number: "",
	},
	address: "",
	referenceNo: "",
	shouldMarkActiveOnPunchIn: true,
	shouldMarkInactiveOnPunchOut: true,
}

export const CreateUserForm: React.FC = () => {
	const [{ fetching }, createUser] = useCreateUserMutation()

	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async (values: CreateUserFormValues) => {
		const { data, error } = await createUser({ input: { ...values, email: values.email?.address ? values.email : undefined, phone: values.phone?.number ? values.phone : undefined } })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.createUser) {
			navigate(`/users/${data.createUser._id}`, { replace: true })

			return
		}
	}

	const formik = useFormik<CreateUserFormValues>({ initialValues, validationSchema, onSubmit })

	const [{ data: userTypesData, error: userTypesError, fetching: userTypesFetching }] = useUserTypesQuery()

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.name && formik.errors.name)} isRequired>
					<FormLabel fontWeight="bold">Name</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter name" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("name")} />

					<FormErrorMessage>{formik.errors.name}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.typeId && formik.errors.typeId)} isRequired>
					<FormLabel fontWeight="bold">Type</FormLabel>

					{userTypesFetching ? (
						<Text>Fetching types</Text>
					) : userTypesError ? (
						<VStack>
							<Text>Couldn&apos;t fetch types</Text>
							<Text>{userTypesError.message.replace("[GraphQL] ", "")}</Text>
						</VStack>
					) : !userTypesData?.userTypes.length ? (
						<VStack>
							<Text>Couldn&apos;t fetch types</Text>
						</VStack>
					) : (
						<Select variant="filled" bgColor="grayscale.input-background" placeholder="Select Type" {...formik.getFieldProps("typeId")}>
							{userTypesData.userTypes.map((type) => (
								<option key={type._id} style={{ backgroundColor: "transparent" }} value={type._id}>
									{type.label.name}
								</option>
							))}
						</Select>
					)}

					<FormErrorMessage>{formik.errors.typeId}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean((formik.touched.email as unknown as EmailInput)?.address && (formik.errors.email as unknown as EmailInput)?.address)}>
					<FormLabel fontWeight="bold">Email</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter email" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("email.address")} />

					<FormErrorMessage>{(formik.errors.email as unknown as EmailInput)?.address}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean((formik.touched.phone as unknown as PhoneInput)?.number && (formik.errors.phone as unknown as PhoneInput)?.number)}>
					<FormLabel fontWeight="bold">Phone</FormLabel>

					<InputGroup as={HStack} spacing={1}>
						<Select flex="3" variant="filled" bgColor="grayscale.input-background" roundedRight="none" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("phone.countryCode")}>
							<option value="91">+91</option>
						</Select>

						<Input flex="7" variant="filled" bgColor="grayscale.input-background" roundedLeft="none" placeholder="Enter phone" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("phone.number")} />
					</InputGroup>
					<FormErrorMessage>{(formik.errors.phone as unknown as PhoneInput)?.number}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.address && formik.errors.address)}>
					<FormLabel fontWeight="bold">Address</FormLabel>

					<Textarea variant="filled" bgColor="grayscale.input-background" placeholder="Enter address" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("address")} />

					<FormErrorMessage>{formik.errors.address}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.referenceNo && formik.errors.referenceNo)} isRequired>
					<FormLabel fontWeight="bold">Reference Number</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter reference number" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("referenceNo")} />

					<FormErrorMessage>{formik.errors.referenceNo}</FormErrorMessage>
				</FormControl>

				<FormControl>
					<Checkbox isChecked={formik.values.shouldMarkActiveOnPunchIn ?? true} onChange={(e) => formik.setFieldValue("shouldMarkActiveOnPunchIn", e.target.checked)}>
						<Text fontWeight="bold">Mark Active on Punch In</Text>
					</Checkbox>
					<Text fontSize="sm" color="gray.600" mt={1}>
						When enabled, user will be marked as Active when they punch in for attendance
					</Text>
				</FormControl>

				<FormControl>
					<Checkbox isChecked={formik.values.shouldMarkInactiveOnPunchOut ?? true} onChange={(e) => formik.setFieldValue("shouldMarkInactiveOnPunchOut", e.target.checked)}>
						<Text fontWeight="bold">Mark Inactive on Punch Out</Text>
					</Checkbox>
					<Text fontSize="sm" color="gray.600" mt={1}>
						When enabled, user will be marked as Inactive when they punch out from attendance
					</Text>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={fetching}>
				Create
			</Button>
		</VStack>
	)
}
